import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getTenantContent } from "../Services/TennantContentService";
import { resolveMediaUrl } from "../utils/seo";

const TenantContentContext = createContext();

export const useTenantContent = () => {
  const context = useContext(TenantContentContext);
  if (!context) {
    throw new Error(
      "useTenantContent must be used within TenantContentProvider"
    );
  }
  return context;
};

export const TenantContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyTheme = useCallback((content) => {
    if (!content) return;

    // Apply CSS variables
    document.documentElement.style.setProperty(
      "--primary-color",
      content.primaryColor
    );
    document.documentElement.style.setProperty(
      "--secondary-color",
      content.secondaryColor
    );

    // Apply favicon
    if (content.faviconUrl) {
      const link =
        document.querySelector("link[rel*='icon']") ||
        document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "shortcut icon";
      link.href = resolveMediaUrl(content.faviconUrl);
      document.getElementsByTagName("head")[0].appendChild(link);
    }
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      const data = await getTenantContent();
      setContent(data);
      applyTheme(data);
    } catch (error) {
      console.error("Error loading tenant content:", error);
    } finally {
      setLoading(false);
    }
  }, [applyTheme]); // applyTheme is a dependency here

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const refreshContent = async () => {
    setLoading(true);
    await fetchContent();
  };

  return (
    <TenantContentContext.Provider value={{ content, loading, refreshContent }}>
      {children}
    </TenantContentContext.Provider>
  );
};
